// SyncProgress — small horizontal progress bar with a stage label.
// Used by the Sources page Sync button (real polling) and the
// checkout-page-node Sync button (synthetic timer for sub-second
// operations where real progress would be theatrical).
import { useEffect, useState } from "react";
import { getApiClient } from "../../lib/apiClient";

interface RealProgressProps {
  shopDomain: string;
  /** Called once when progress.done becomes true, regardless of error. */
  onComplete?: (errored: boolean) => void;
  pollMs?: number;
}

/** RealSyncProgress polls the backend's sync-progress endpoint while a
 *  big sync is running. Stops polling when status is `done` or `failed`. */
export function RealSyncProgress({ shopDomain, onComplete, pollMs = 1000 }: RealProgressProps) {
  const [percent, setPercent] = useState(0);
  const [label, setLabel] = useState("Starting…");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let firedComplete = false;
    const tick = async () => {
      if (cancelled) return;
      try {
        const p = await getApiClient().shopify.syncProgress(shopDomain);
        if (cancelled) return;
        if (p.percent > percent) setPercent(p.percent);
        if (p.stage_label) setLabel(p.stage_label);
        if (p.error) setError(p.error);
        if (p.done) {
          if (!firedComplete) {
            firedComplete = true;
            onComplete?.(!!p.error);
          }
          return; // stop polling
        }
      } catch {
        // transient — keep polling, don't surface
      }
      setTimeout(tick, pollMs);
    };
    void tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopDomain]);

  return <ProgressView percent={percent} label={error ? "Sync failed: " + error : label} errored={!!error} />;
}

interface SyntheticProps {
  /** When the real operation finishes, set this to true. The bar
   *  jumps to 100% and the parent unmounts the component. */
  done: boolean;
  errored?: boolean;
  /** Expected duration in ms — bar fills proportionally up to 95% over
   *  this window, holds at 95% until `done` flips, then jumps to 100%. */
  expectedDurationMs?: number;
  label?: string;
}

/** SyntheticSyncProgress is for fast operations (sub-second to a few
 *  seconds) where there's no honest backend progress to report. The
 *  bar tweens up to 95% of its expected duration; on `done` it jumps
 *  to 100%. Honest within the bounds of the expected duration. */
export function SyntheticSyncProgress({ done, errored, expectedDurationMs = 1500, label = "Syncing…" }: SyntheticProps) {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    if (done) {
      setPercent(100);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(95, Math.round((elapsed / expectedDurationMs) * 95));
      setPercent(pct);
      if (pct >= 95) clearInterval(id);
    }, 50);
    return () => clearInterval(id);
  }, [done, expectedDurationMs]);

  return <ProgressView percent={percent} label={errored ? "Sync failed" : done ? "Done" : label} errored={!!errored} />;
}

function ProgressView({ percent, label, errored }: { percent: number; label: string; errored: boolean }) {
  return (
    <div className="space-y-1">
      <div className="h-1.5 w-full bg-neutral-200 dark:bg-neutral-800 rounded-full overflow-hidden">
        <div
          className={`h-full transition-all duration-200 ${errored ? "bg-red-500" : "bg-emerald-500"}`}
          style={{ width: `${percent}%` }}
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      <div className="flex items-center justify-between text-[11px] text-neutral-500 dark:text-neutral-400">
        <span>{label}</span>
        <span>{percent}%</span>
      </div>
    </div>
  );
}
